import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { extractErrorMessage } from "@/core/api/errors";
import type { OnboardingStepPane } from "@/onboarding/routes";
import { documentCategory } from "./document-categories";
import { DocumentGuidance } from "./document-guidance";
import { DocumentList } from "./document-list";
import { DocumentSection } from "./document-section";
import { groupOnboardingDocuments } from "./document-utils";
import { ReadyChecklist } from "./documents-ready-card";
import { useOnboardingDocuments, useSetDocumentsReady } from "./query";
import { StepLayout } from "./step-layout";
import { SubmissionConfirmModal } from "./submission-confirm-modal";
import { ONBOARDING_DOCUMENT_TYPE } from "./types";

interface DocumentStepScreenBaseProps {
  businessId: string;
  activePane: OnboardingStepPane;
  onBack: () => void;
}

type DocumentStepScreenProps = DocumentStepScreenBaseProps &
  (
    | {
        type: typeof ONBOARDING_DOCUMENT_TYPE.referenceData;
        onContinue: () => void;
        documentsReadyAt?: never;
        onSubmitted?: never;
      }
    | {
        type: typeof ONBOARDING_DOCUMENT_TYPE.payPlan;
        onContinue?: never;
        documentsReadyAt: string | null | undefined;
        onSubmitted: () => void;
      }
  );

// One kind of document per step. Splitting them means each page can spell out its own ask
// in full instead of the two competing for one screen, and neither can be mistaken for the
// other. Nothing here gates uploading: the pay plan is required to say you are finished,
// which is the last step's business, not this one's.
export const DocumentStepScreen = ({
  businessId,
  type,
  activePane,
  onBack,
  onContinue,
  documentsReadyAt,
  onSubmitted,
}: DocumentStepScreenProps) => {
  const [readyError, setReadyError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const readyMutation = useSetDocumentsReady(businessId);

  const submit = () => {
    setReadyError(null);
    readyMutation.mutate(true, {
      onSuccess: () => {
        setIsConfirming(false);
        onSubmitted?.();
      },
      onError: (error) => setReadyError(extractErrorMessage(error).message),
    });
  };
  const documentsQuery = useOnboardingDocuments(businessId);
  const { payPlans, referenceData, untyped } = groupOnboardingDocuments(
    documentsQuery.data,
  );
  const mine =
    type === ONBOARDING_DOCUMENT_TYPE.payPlan ? payPlans : referenceData;
  const hasPayPlan = payPlans.length > 0;
  const isPayPlanStep = type === ONBOARDING_DOCUMENT_TYPE.payPlan;
  const category = documentCategory(type);

  // Grouped rather than pushed to opposite edges: two buttons a screen apart read as two
  // stranded controls, and a pair reads as one decision.
  const nav = (
    <Stack direction="row" gap="sm" justify="end" align="center" fullWidth>
      <Button type="button" variant="tertiary" size="sm" onClick={onBack}>
        Back
      </Button>
      {/* Always available: uploading is the ask, but nobody is held on this page until
          they satisfy it. */}
      {onContinue && (
        <Button type="button" size="sm" onClick={onContinue}>
          Continue
        </Button>
      )}

      {/* The last step's Continue is the handshake. Without a pay plan there is no action
          here at all: the checklist says what is missing, and the page offers no way
          around it. */}
      {isPayPlanStep && !documentsReadyAt && hasPayPlan && (
        <Button type="button" size="sm" onClick={() => setIsConfirming(true)}>
          I have sent everything
        </Button>
      )}
    </Stack>
  );

  return (
    <StepLayout
      activePane={activePane}
      title={category.stepTitle}
      subtitle={category.stepSubtitle}
      nav={nav}
    >
      {isPayPlanStep && !documentsReadyAt && (
        <ReadyChecklist
          hasPayPlan={hasPayPlan}
          hasReferenceData={referenceData.length > 0}
          error={readyError}
        />
      )}

      {/* Above the uploader: on a step whose whole job is asking for a kind of document,
          what to send is the thing to read before dropping anything. */}
      <DocumentGuidance type={type} />

      <DocumentSection
        businessId={businessId}
        type={type}
        documents={mine}
        isLoading={documentsQuery.isLoading}
        isError={documentsQuery.isError}
      />

      {isPayPlanStep && untyped.length > 0 && (
        <Card className="w-full border-stroke-tertiary shadow-sm">
          <CardHeader>
            <Typography variant="subtitleW1">Other documents</Typography>
          </CardHeader>
          <CardContent>
            <DocumentList businessId={businessId} documents={untyped} />
          </CardContent>
        </Card>
      )}

      <SubmissionConfirmModal
        open={isConfirming}
        isSaving={readyMutation.isPending}
        error={readyError}
        onCancel={() => setIsConfirming(false)}
        onConfirm={submit}
      />
    </StepLayout>
  );
};
